import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";

/**
 * JourneyVideo — cinematic full-bleed video block that plays "Our Journey"
 * vertical reel. Silent-autoplay by default (browsers require muted for
 * autoplay); user can tap to unmute + play/pause. Sits between the Founder
 * story and the Product Teaser on the homepage.
 */
export const JourneyVideo = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <section
      data-testid="journey-video-section"
      className="relative py-24 md:py-36 bg-[#0A0A0C] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="section-mark" />
          <span className="overline">The Journey · In Motion</span>
          <span className="section-mark" />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Left — copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5"
          >
            <h2 className="h-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mb-8">
              Not a product launch.
              <br />
              <span className="text-[#F47A20]">A journey.</span>
            </h2>
            <p className="text-white/72 text-lg leading-relaxed mb-5 max-w-xl">
              Every ride sharpens the design. Every stitch has a story. The OUTLAW V1 isn&rsquo;t coming out of a boardroom &mdash; it&rsquo;s coming off the highway, the workshop floor and the tail end of a long day in the saddle.
            </p>
            <p className="text-white/55 text-[15px] leading-relaxed max-w-xl">
              Press play &mdash; and turn the sound on. John tells the story himself.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                data-testid="journey-video-play-toggle"
                aria-label={playing ? "Pause" : "Play"}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 bg-[#0E0E11] hover:border-[#F47A20]/55 hover:bg-[#F47A20]/[0.07] transition-colors rounded-sm text-white/85"
              >
                {playing ? <Pause size={14} /> : <Play size={14} />}
                <span className="text-[12px] tracking-[0.15em] uppercase font-bold">
                  {playing ? "Pause" : "Play"}
                </span>
              </button>
              <button
                type="button"
                onClick={toggleMute}
                data-testid="journey-video-mute-toggle"
                aria-label={muted ? "Unmute" : "Mute"}
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 bg-[#0E0E11] hover:border-[#F47A20]/55 hover:bg-[#F47A20]/[0.07] transition-colors rounded-sm text-white/85"
              >
                {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
                <span className="text-[12px] tracking-[0.15em] uppercase font-bold">
                  {muted ? "Unmute" : "Mute"}
                </span>
              </button>
            </div>
          </motion.div>

          {/* Right — vertical video */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
            className="lg:col-span-7"
          >
            <div className="relative mx-auto max-w-[440px] lg:max-w-[520px] aspect-[3/4] overflow-hidden rounded-sm border border-white/10 bg-[#141417] shadow-[0_40px_100px_-40px_rgba(244,122,32,0.35)]">
              <div className="pointer-events-none absolute top-3 left-3 z-10 inline-flex items-center gap-2 px-2.5 py-1 bg-[#0A0A0C]/70 backdrop-blur border border-[#F47A20]/35 rounded-sm">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#F47A20] opacity-60 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F47A20]" />
                </span>
                <span className="serial text-[9px] tracking-[0.25em] text-[#F47A20] font-bold">
                  OUR STORY &middot; NARRATED BY JOHN
                </span>
              </div>
              <video
                ref={videoRef}
                data-testid="journey-video-player"
                src="/media/story-v2.mp4"
                poster="/media/story-v2-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default JourneyVideo;
